import {useState, useEffect} from 'react'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {DownloadAppReminderContent} from '@/customComponents/BookRideSteps/Components/downloadAppReminderContent.jsx'
import {getDevice, openTripInAppOrStore, STORE_URLS} from '@/utils/deepLink.js'

/**
 * Shared "Track Driver" button + behavior. Used by the booking-received step and the
 * trip-details driver card.
 *  - Real desktop → download-app popup (the app can't run there).
 *  - Android / iOS → open the app on this trip (hopn://ride/:id), falling back to the
 *    store if the app isn't installed. Shows a spinner during the handoff.
 *
 * Style the button via `className` + `children`. Pass `loaderColor` (e.g. "#fff") when the
 * button sits on a dark background so the spinner is visible.
 */
export default function TrackDriverButton({tripId, className = '', children = 'Track Driver', loaderColor}) {
    const [showPopup, setShowPopup] = useState(false)
    const [loading, setLoading] = useState(false)

    // The app/store handoff backgrounds the tab; clear the spinner when the user returns.
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') setLoading(false) }
        const onPageShow = (e) => { if (e.persisted) setLoading(false) }
        document.addEventListener('visibilitychange', onVisible)
        window.addEventListener('pageshow', onPageShow)
        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            window.removeEventListener('pageshow', onPageShow)
        }
    }, [])

    const handleClick = () => {
        const device = getDevice()
        // Desktop → the download-app popup (the app can't run there).
        if (device === 'desktop') {
            setShowPopup(true)
            return
        }
        // A phone with nothing to deep-link to (the terminal guide is reachable long
        // before a booking exists) → straight to the store. A QR code is useless on the
        // phone that would be scanning it, so "get the app" is the whole action.
        if (!tripId) {
            window.location.href = STORE_URLS[device]
            return
        }
        // Immediate feedback for the ~1s+ handoff; safety timeout in case neither the app nor
        // the store took over.
        setLoading(true)
        openTripInAppOrStore(tripId, device)
        setTimeout(() => setLoading(false), 4000)
    }

    return (
        <>
            <button type="button" className={className} onClick={handleClick} disabled={loading}>
                {loading ? (loaderColor ? <Loader color={loaderColor}/> : <Loader/>) : children}
            </button>
            <Modal isOpen={showPopup} onClose={() => setShowPopup(false)}>
                <ModalHeader title="Track Driver" onClose={() => setShowPopup(false)}/>
                <DownloadAppReminderContent onDone={() => setShowPopup(false)}/>
            </Modal>
        </>
    )
}

// `GetAppButton` lived here — a "Get the app" CTA that rendered ONLY inside the native
// app and returned null in every browser. Its one caller, the Why-Choose-Us "Live
// tracking" perk, now uses TrackDriverButton above so the CTA is visible on the web too:
// desktop opens the download popup, a phone hands off to the app/store. Nothing else
// referenced it, so it is gone rather than left dead.
